import { useState } from 'react';
import type { PartnerConfig, ChatState, CustomerInfo } from '../types';
import type { PendingFile } from './FilePreview';
import { Header } from './Header';
import { MessageList } from './MessageList';
import { InputArea } from './InputArea';
import { ContactDrawer } from './ContactDrawer';
import { StatusMessage } from './StatusMessage';
import { LanguageEar } from './LanguageEar';
import { DropOverlay } from './DropOverlay';
import { FilesDrawer } from './FilesDrawer';
import { ResearchEar } from './ResearchEar';
import { ResearchDrawer } from './ResearchDrawer';

interface LanguageState {
  current: string;
  cf_language: string | null;
  show_cf_suggestion: boolean;
}

interface ChatWindowLegacyProps {
  config: PartnerConfig;
  onSendMessage: (message: string) => void;
  state: ChatState;
  onCustomerSubmit: (customer: Partial<CustomerInfo>) => void;
  onDrawerDismiss: () => void;
  onDrawerOpen: () => void;
  pendingFiles: PendingFile[];
  onFilesSelected: (files: File[]) => void;
  onFileRemove: (id: string) => void;
  // Feature flags
  fileUploadEnabled?: boolean;
  researchModeEnabled?: boolean;
  // Language props
  languageState?: LanguageState;
  onSelectLanguage?: (code: string) => void;
  onDismissLanguageSuggestion?: () => void;
  // Research mode props
  onVerifyEmail?: () => void;
  onConfirmOtp?: (code: string) => void;
  onResendOtp?: () => void;
  onToggleResearchMode?: (enabled: boolean) => void;
  onUpdateNotificationPrefs?: (prefs: { browser: boolean; email: boolean }) => void;
}

export function ChatWindowLegacy({
  config,
  onSendMessage,
  state,
  onCustomerSubmit,
  onDrawerDismiss,
  onDrawerOpen,
  pendingFiles,
  onFilesSelected,
  onFileRemove,
  fileUploadEnabled = true,
  researchModeEnabled = true,
  languageState,
  onSelectLanguage,
  onDismissLanguageSuggestion,
  onVerifyEmail,
  onConfirmOtp,
  onResendOtp,
  onToggleResearchMode,
  onUpdateNotificationPrefs,
}: ChatWindowLegacyProps) {
  const [filesDrawerOpen, setFilesDrawerOpen] = useState(false);
  const [researchDrawerOpen, setResearchDrawerOpen] = useState(false);

  const supportedLanguages = config.supported_languages || [];
  const showLanguageEar = supportedLanguages.length > 1 && languageState && onSelectLanguage;

  // Mutual exclusivity: close other drawers when opening one
  const handleResearchDrawerToggle = () => {
    if (!researchDrawerOpen) {
      // Opening research drawer - close other drawers
      if (state.showContactDrawer) onDrawerDismiss();
      setFilesDrawerOpen(false);
    }
    setResearchDrawerOpen(!researchDrawerOpen);
  };

  const handleFilesDrawerToggle = () => {
    if (!filesDrawerOpen) {
      // Opening files drawer - close contact drawer
      if (state.showContactDrawer) {
        onDrawerDismiss();
      }
      setResearchDrawerOpen(false);
    }
    setFilesDrawerOpen(!filesDrawerOpen);
  };

  const handleContactDrawerOpen = () => {
    // Opening contact drawer - close files drawer
    setFilesDrawerOpen(false);
    setResearchDrawerOpen(false);
    onDrawerOpen();
  };

  return (
    <DropOverlay
      onFilesSelected={onFilesSelected}
      disabled={!state.isConnected}
    >
      <div
        className="flex flex-col h-full bg-white rounded-lg shadow-lg overflow-hidden relative"
        style={{
          '--color-primary-override': config.branding.colors.primary,
          '--color-secondary-override': config.branding.colors.secondary,
          '--color-background-override': config.branding.colors.background,
          '--color-text-override': config.branding.colors.text,
        } as React.CSSProperties}
      >
        <Header config={config} />
        <MessageList
          messages={state.messages}
          isTyping={state.isTyping}
          streamingMessage={state.streamingMessage}
        />
        <StatusMessage message={state.statusMessage} />
        <InputArea
          onSend={onSendMessage}
          isConnected={state.isConnected}
          pendingFiles={pendingFiles}
          onFileRemove={onFileRemove}
          language={languageState?.current || config.language}
        />
        {config.branding.show_powered_by && (
          <div className="text-center text-xs text-gray-400 py-2 border-t">
            Powered by HW group
          </div>
        )}

        {/* Research ear - positioned at top of ear stack (only if research mode enabled) */}
        {researchModeEnabled && (
          <ResearchEar
            state={state.researchMode}
            customerEmail={state.customer?.email || null}
            onToggle={handleResearchDrawerToggle}
          />
        )}

        {/* Research drawer (only if research mode enabled) */}
        {researchModeEnabled && (
          <ResearchDrawer
            isOpen={researchDrawerOpen}
            state={state.researchMode}
            customerEmail={state.customer?.email || null}
            onClose={() => setResearchDrawerOpen(false)}
            onVerifyEmail={onVerifyEmail || (() => {})}
            onConfirmOtp={onConfirmOtp || (() => {})}
            onResendOtp={onResendOtp || (() => {})}
            onToggleMode={onToggleResearchMode || (() => {})}
            onUpdateNotificationPrefs={onUpdateNotificationPrefs || (() => {})}
            onOpenContactDrawer={handleContactDrawerOpen}
          />
        )}

        {/* Files drawer - positioned above language ear (only if file upload enabled) */}
        {fileUploadEnabled && (
          <FilesDrawer
            isOpen={filesDrawerOpen}
            onToggle={handleFilesDrawerToggle}
            files={pendingFiles}
            onRemove={onFileRemove}
            onFilesSelected={onFilesSelected}
            disabled={!state.isConnected}
          />
        )}

        {/* Language ear - positioned above contact drawer */}
        {showLanguageEar && (
          <LanguageEar
            currentLanguage={languageState.current}
            cfLanguage={languageState.cf_language}
            showCfSuggestion={languageState.show_cf_suggestion}
            supportedLanguages={supportedLanguages}
            onSelectLanguage={onSelectLanguage}
            onDismissSuggestion={onDismissLanguageSuggestion || (() => {})}
          />
        )}

        {/* Contact drawer with icon handle (always visible) */}
        <ContactDrawer
          isOpen={state.showContactDrawer}
          countries={config.contact_form.countries}
          defaultCountry={config.contact_form.default_country}
          onSubmit={onCustomerSubmit}
          onDismiss={onDrawerDismiss}
          onOpen={handleContactDrawerOpen}
          triggerIntent={state.drawerTriggerIntent}
          existingCustomer={state.customer}
          privacyPolicyUrl={config.privacy_policy_url}
          language={languageState?.current || config.language}
        />
      </div>
    </DropOverlay>
  );
}
